import { useEffect, useMemo, useRef, useState } from "react";
import { getMethod2 } from "../functions/httpMethods";
import ReusableAutocompleteWithID from "./general/ReusableAutocompleteWithID";
import ReusableSelect from "./general/ReusableSelect";

const EnhancedFilter = ({
  label,
  endpoint,
  value,
  onChange,
  dependencies,
  width = 200,
  marginRight = 40,
  disabled = false,
  // Opt-in local search (renders a searchable autocomplete instead of a plain select)
  searchable = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const cache = useRef({});
  // Remember the label for a chosen value so it stays visible even if the
  // options list refreshes (e.g. when a sibling filter changes the endpoint).
  const selectedLabels = useRef({});

  const fetchOptions = async () => {
    if (fetched) return;
    setLoading(true);

    await getMethod2(endpoint, "").then((response) => {
      setFetched(true);
      cache.current[endpoint] = response;
      setOptions(response);
      setLoading(false);
    });
  };

  // Reset fetched state when endpoint changes
  useEffect(() => {
    setFetched(false);
  }, [endpoint]);

  // De-duplicated, empty-label-free options for the searchable autocomplete.
  // (Duplicate/empty labels cause blank gaps in large MUI Autocomplete lists.)
  const autocompleteOptions = useMemo(() => {
    const seen = new Set();
    const list = [];

    options.forEach((o) => {
      if (o?.filter_id === undefined || o.filter_id === null) return;
      const key = String(o.filter_id);
      if (seen.has(key)) return;
      const name = String(o.filter_name ?? "").trim();
      if (!name) return;
      seen.add(key);
      list.push({ id: o.filter_id, option: name });
    });

    // Keep the current selection visible if it isn't in the freshly fetched list
    if (
      value !== "" &&
      value !== null &&
      value !== undefined &&
      !list.some((o) => String(o.id) === String(value)) &&
      selectedLabels.current[value]
    ) {
      list.unshift({ id: value, option: selectedLabels.current[value] });
    }

    return list;
  }, [options, value]);

  const handleAutocompleteChange = (nextValue) => {
    if (nextValue !== "" && nextValue !== null && nextValue !== undefined) {
      const match = autocompleteOptions.find(
        (o) => String(o.id) === String(nextValue)
      );
      if (match) selectedLabels.current[nextValue] = match.option;
    }
    onChange(nextValue);
  };

  if (searchable) {
    return (
      <div onFocus={fetchOptions}>
        <ReusableAutocompleteWithID
          type="default"
          width={width}
          height={54}
          marginRight={marginRight}
          bgColor="#FFF"
          borderColor="#707070"
          placeholder={label}
          state={value}
          setState={handleAutocompleteChange}
          disabled={disabled}
          grayedOut={disabled}
          readOnly={disabled}
          options={autocompleteOptions}
        />
      </div>
    );
  }

  return (
    <>
      <ReusableSelect
        loading={loading}
        onFocus={fetchOptions}
        type={"filter"}
        label={label}
        width={width}
        marginRight={marginRight}
        bgColor="#FFF"
        state={value}
        setState={onChange}
        disabled={disabled}
        grayed={disabled}
        options={options.map((o) => ({
          value: o.filter_id,
          option: o.filter_name,
        }))}
      />
    </>
  );
};

export default EnhancedFilter;
